import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Info, X, Clock, Save, Loader2 } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RECAP_FIELDS, DEMARRAGE, emptyRecap, fmtLongDate, toIso, todayIso } from './recapUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const lastDays = (n) => Array.from({ length: n }, (_, i) => { const d = new Date(); d.setDate(d.getDate() - i); return toIso(d); });

export function RecapDialog({ open, onOpenChange, userEmail, canEdit, initialDate, onSaved, onShowHistory }) {
  const [date, setDate] = useState(initialDate || todayIso());
  const [form, setForm] = useState(emptyRecap());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setDate(initialDate || todayIso()); }, [open, initialDate]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/api/recaps/day`, { params: { date, user_email: userEmail } })
      .then((r) => setForm(r.data?.id ? { ...emptyRecap(), ...r.data } : (canEdit ? emptyRecap() : { ...emptyRecap(), activites: '' })))
      .catch(() => setForm(emptyRecap()))
      .finally(() => setLoading(false));
  }, [open, date, userEmail, canEdit]);

  const days = lastDays(14);
  if (!days.includes(date)) days.push(date);

  const save = async () => {
    if (!form.activites.trim() || form.activites.trim() === DEMARRAGE) { toast.error('Décrivez vos activités réalisées'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/recaps`, { date, ...Object.fromEntries(RECAP_FIELDS.map((f) => [f.key, form[f.key] || ''])) });
      toast.success('Récapitulatif enregistré');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 max-h-[94vh] overflow-hidden flex flex-col [&>button]:hidden" data-testid="recap-dialog">
        <div className="flex items-start gap-3 px-6 py-5 border-b border-slate-100">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><CalendarDays className="h-6 w-6" /></div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-slate-900">Récapitulatif du jour</h2>
            <p className="text-sm text-slate-500">{canEdit ? "Rédigez votre bilan quotidien d'activité." : "Bilan quotidien d'activité (lecture seule)."}</p>
          </div>
          <button onClick={() => onOpenChange(false)} className="p-1 rounded-md text-slate-500 hover:bg-slate-100 transition-colors" data-testid="recap-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-[120px_1fr] items-center gap-3">
            <span className="font-semibold text-slate-800">Date</span>
            <Select value={date} onValueChange={setDate}>
              <SelectTrigger className="h-11" data-testid="recap-date-select">
                <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-500" /><SelectValue /></span>
              </SelectTrigger>
              <SelectContent>
                {days.map((d) => (
                  <SelectItem key={d} value={d}>{d === todayIso() ? `Aujourd'hui – ${fmtLongDate(d)}` : fmtLongDate(d)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5 text-sm text-blue-700">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span><b>Démarrage de la journée :</b> prise de connaissance des priorités et organisation des tâches.</span>
          </div>

          {!loading && !canEdit && !form.id && (
            <p className="text-center py-3 text-sm text-slate-500 bg-slate-50 rounded-lg" data-testid="recap-none">Aucun récapitulatif pour cette date.</p>
          )}
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : RECAP_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block font-semibold text-slate-800 mb-1.5">{f.label}{f.required && <span className="text-rose-500"> *</span>}</label>
              <textarea rows={f.rows} value={form[f.key] || ''} readOnly={!canEdit} placeholder={canEdit ? f.placeholder : '—'}
                onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-shadow read-only:bg-slate-50"
                data-testid={`recap-field-${f.key}`} />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onShowHistory} className="h-11 px-5 inline-flex items-center gap-2 rounded-lg border border-blue-300 text-blue-700 font-semibold hover:bg-blue-50 transition-colors" data-testid="recap-history-btn">
            <Clock className="h-4 w-4" /> Historique
          </button>
          {canEdit && (
            <button onClick={save} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60" data-testid="recap-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer le récapitulatif
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
